import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Container, Typography, Grid, Card, Button, Stack } from '@mui/material';
import CallToAction from '../widgets/CallToAction';
import { keyframes } from '@mui/system';

// Page refondue pour mettre en avant la plateforme étudiante (web + app mobile)
// Prévoit des espaces photos (placeholders) facilement remplaçables par des <img /> ou composants spécifiques.

export default function PresentationEtudiant() {
    // Données pour l'affichage interactif des fonctionnalités (section remplaçant "Comment ça marche ?")
    const featureShowcase = [
        {
            key: 'ia-interview',
            title: 'Entretien IA rapide',
            desc: "Passe un entretien de quelques minutes pour que notre IA te comprenne vraiment. Récupère un CV parfait et des offres ciblées.",
            image: "/assets/images/plateforme_student/1.png"
        },
        {
            key: 'matching',
            title: 'Matching quotidien',
            desc: "Notre IA analyse des milliers d'offres chaque jour pour te proposer les plus pertinentes selon ton profil et tes objectifs.",
            image: '/assets/images/plateforme_student/2.png'
        },
        {
            key: 'messagerie',
            title: 'Échange avec des recruteurs, étudiants et diplômés',
            desc: "Chat en temps réel, notifications et suivi des conversations avec des pros, étudiants et alumni.",
            image: '/assets/images/plateforme_student/3.png'
        },
        {
            key: 'suivi-candidatures',
            title: 'Suivi des candidatures',
            desc: "Organise statuts, relances et échanges pour rester au clair.",
            image: '/assets/images/plateforme_student/4.png'
        },
        {
            key: 'reseau-evenements',
            title: 'Plus de 100 000 offres',
            desc: " Retrouve les meilleures offres de stage, d’alternance et d’emploi chaque jour.",
            image: '/assets/images/plateforme_student/5.png'
        }
    ];
    // Helper pour générer un texte alternatif pertinent pour les logos
    const altFromSrc = (s) => {
        try {
            const file = s.split('/').pop() || '';
            const base = file.split('.')[0] || '';
            return `Logo ${base.replace(/[-_]/g, ' ')}`;
        } catch {
            return 'Logo entreprise';
        }
    };

    const [selectedFeatureIndex, setSelectedFeatureIndex] = useState(0);
    // Ancienne section "process" supprimée au profit d'une section interactive



    const humanSectionBullets = [
        'Messagerie temps réel claire et moderne',
        'Vidéo de présentation intégrée aux candidatures',
        'Mise en relation qualitative (pro / alumni / étudiants)',
        'Automatisation des tâches répétitives pour libérer ton temps'
    ];

    // Logos d'entreprises (remplace par tes vrais logos dans /public/assets/logos)
    const companyLogos = [
        '/assets/images/entreprises/accenture.png',
        '/assets/images/entreprises/bnp.jpg',
        '/assets/images/entreprises/Dassault.png',
        '/assets/images/entreprises/EDF.png',
        '/assets/images/entreprises/EY.png',
        '/assets/images/entreprises/puy-du-fou.png',
        '/assets/images/entreprises/toyota.png',
    ];

    // Animation de défilement horizontal infini
    const scrollX = keyframes`
        0% { transform: translateX(0); }
        100% { transform: translateX(-50%); }
    `;

    return (
        // Harmonized with Solutions_ecoles top padding (pt: { xs:4, md:6 })
        <Box sx={{ backgroundColor: '#fff', pt: { xs: 4, md: 6 }, pb: 10 }}> {/* Global background wrapper */}
            <Helmet>
                <title>Étudiants — Career center IA: CV, stage, alternance | Student Link</title>
                <meta name="description" content="Passe un entretien IA, génère ton CV et reçois des offres de stage, alternance et emploi ciblées chaque jour. Web + app mobile." />
                <link rel="canonical" href="https://studentlink.fr/etudiants" />
            </Helmet>
            {/* HERO */}
            <Container maxWidth="lg">
                {/* Align hero bottom margin with Solutions_ecoles (mb: { xs:8, md:10 }) */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: { xs: 8, md: 10 } }}>
                    <Grid item xs={12} md={6}>

                        <Typography variant="h2" component="h1" sx={{ fontWeight: 700, lineHeight: 1.1, mt: 1, fontSize: { xs: 42, md: 56 } }}>
                            Passe l’entretien, reçois les bonnes offres, signe plus vite !
                        </Typography>
                        <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary', fontWeight: 400 }}>
                            Passe un entretien IA de quelques minutes et reçois un CV parfait, une vidéo de présentation et des offres ciblées chaque jour. Disponible sur web et mobile.
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
                            <a href="https://apps.apple.com/fr/app/student-link/id6447442323" target="_blank" rel="noopener noreferrer">
                                <Box
                                    component="img"
                                    src="/assets/images/app-store.png"
                                    alt="Télécharger dans l’App Store"
                                    loading="lazy"
                                    decoding="async"
                                    sx={{ height: 48, width: 'auto' }}
                                />
                            </a>
                            <a href="https://play.google.com/store/search?q=student%20link&c=apps" target="_blank" rel="noopener noreferrer">
                                <Box
                                    component="img"
                                    src="/assets/images/google-play.png"
                                    alt="Disponible sur Google Play"
                                    loading="lazy"
                                    decoding="async"
                                    sx={{ height: 48, width: 'auto' }}
                                />
                            </a>
                        </Stack>

                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* Placeholder visuel aligné avec la page étudiant */}
                        <Box sx={{
                            position: 'relative',
                            borderRadius: 8,
                            height: { xs: 300, md: 420 },
                            // background: 'linear-gradient(135deg,#0075FF 0%,#00B2FF 100%)',
                            // boxShadow: '0 10px 35px rgba(0,117,255,0.25)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden'
                        }}>
                            <Box
                                component="img"
                                src="/assets/images/plateforme_student/placeholder.jpg"
                                alt="Interface student Student Link"
                                loading="lazy"
                                decoding="async"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    objectPosition: 'left center',
                                    borderRadius: 8,
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* FONCTIONNALITÉS INTERACTIVES */}
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: 34, md: 44 } }}>Décroche le contrat de tes rêves</Typography>

                </Box>
                <Grid container spacing={6} alignItems="stretch">
                    {/* Colonne gauche: 5 fonctionnalités dans des rectangles */}
                    <Grid item xs={12} md={6}>
                        <Stack spacing={2} role="listbox" aria-label="Fonctionnalités">
                            {featureShowcase.map((f, idx) => {
                                const active = idx === selectedFeatureIndex;
                                return (
                                    <Card
                                        key={f.key}
                                        onClick={() => setSelectedFeatureIndex(idx)}
                                        role="option"
                                        aria-selected={active}
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setSelectedFeatureIndex(idx);
                                            }
                                        }}
                                        sx={{
                                            p: 2.5,
                                            borderRadius: 8,
                                            cursor: 'pointer',
                                            border: active ? '2px solid' : '1px solid',
                                            borderColor: active ? 'primary.main' : 'divider',
                                            boxShadow: active ? '0 6px 18px rgba(0,117,255,0.15)' : 'none',
                                            transition: 'all .2s ease',
                                            '&:hover': { boxShadow: '0 6px 18px rgba(0,0,0,0.06)' }
                                        }}
                                    >
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{f.title}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>{f.desc}</Typography>
                                    </Card>
                                );
                            })}
                        </Stack>
                    </Grid>

                    {/* Colonne droite: image qui change selon la fonctionnalité */}
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                position: 'relative',
                                borderRadius: 8,
                                // Aligne la hauteur avec la colonne des fonctionnalités à droite dès md+
                                height: { xs: 260, md: '100%' },
                                backgroundColor: '#f5f7fb',
                                border: '1px solid',
                                borderColor: 'divider',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            aria-live="polite"
                        >
                            <Box
                                component="img"
                                src={featureShowcase[selectedFeatureIndex].image}
                                alt={featureShowcase[selectedFeatureIndex].title}
                                loading="lazy"
                                decoding="async"
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    objectPosition: 'center'
                                }}
                            />
                        </Box>

                    </Grid>
                </Grid>
            </Container>

            {/* Offer GRID */}
            <Container maxWidth="lg" sx={{ mt: 12 }}>
                <Box sx={{ mb: 6, textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: 32, md: 42 } }}>+ 100 000 offres de stage, d’alternance et d’emploi sont analysées par notre IA !</Typography>
                    <Typography variant="body1" sx={{ mt: 2, maxWidth: 760, mx: 'auto', color: 'text.secondary' }}>
                        Student Link te propose les meilleures offres chaque jour. C’est facile.
                    </Typography>
                </Box>

                {/* Marquee des logos: bandeau à défilement latéral infini */}
                <Box sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    py: { xs: 2, md: 3 },
                }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: 'max-content',
                            gap: { xs: 6, md: 8 },
                            animation: `${scrollX} linear infinite`,
                            animationDuration: { xs: '50s', md: '40s' },
                            '@media (prefers-reduced-motion: reduce)': {
                                animation: 'none'
                            },
                        }}
                    >
                        {[...companyLogos, ...companyLogos].map((src, i) => (
                            <Box
                                key={`logo-${i}`}
                                component="img"
                                src={src}
                                alt={altFromSrc(src)}
                                loading="lazy"
                                decoding="async"
                                sx={{
                                    height: { xs: 28, sm: 36, md: 44 },
                                    width: 'auto',
                                    filter: 'grayscale(100%)',
                                    opacity: 0.85,
                                    transition: 'all .2s ease',
                                    '&:hover': { filter: 'none', opacity: 1 }
                                }}
                            />
                        ))}
                    </Box>

                    {/* Fades aux bords pour un effet propre */}
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: 0,
                        width: 64,
                        pointerEvents: 'none',
                        background: 'linear-gradient(90deg, #ffffff 0%, rgba(255,255,255,0) 100%)'
                    }} />
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        right: 0,
                        width: 64,
                        pointerEvents: 'none',
                        background: 'linear-gradient(270deg, #ffffff 0%, rgba(255,255,255,0) 100%)'
                    }} />
                </Box>

            </Container>



            {/* HUMAIN AU CENTRE */}
            <Container maxWidth="lg" sx={{ mt: 14 }}>
                <Grid container spacing={4} alignItems="stretch">
                    {/* Colonne gauche: image handshake */}
                    <Grid item xs={12} md={6}>
                        <Box
                            component="img"
                            src="/assets/images/handshake.jpg"
                            alt="Poignée de main — collaboration"
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = '/assets/images/team.jpg';
                            }}
                            sx={{
                                width: '100%',
                                height: { xs: 240, md: 420 },
                                objectFit: 'cover',
                                borderRadius: 8,
                                border: '1px solid',
                                borderColor: 'divider'
                            }}
                        />
                    </Grid>
                    {/* Colonne droite: contenu texte sans boîte */}
                    <Grid item xs={12} md={6} sx={{ display: 'flex', minHeight: { xs: 240, md: 420 } }}>
                        <Box sx={{ p: { xs: 0, md: 1 }, height: { xs: 'auto', md: '100%' }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Typography variant="h3" sx={{ fontWeight: 700, mt: 1, fontSize: { xs: 30, md: 40 } }}>L’humain avant tout</Typography>
                            <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                                Nous automatisons ce qui est répétitif (CV, recherche, tri, suivi) pour que tu investisses ton temps dans les échanges authentiques et la mise en valeur de ta personnalité.
                            </Typography>
                            <Grid container spacing={2} sx={{ mt: 3 }}>
                                {humanSectionBullets.map(b => (
                                    <Grid item xs={12} sm={6} key={b}>
                                        <Stack direction="row" spacing={1} alignItems="flex-start">
                                            <Box sx={{ fontSize: 18, lineHeight: '22px' }}>✅</Box>
                                            <Typography variant="body2" sx={{ lineHeight: 1.4 }}>{b}</Typography>
                                        </Stack>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* CTA SECTION */}
            <Container maxWidth="lg" sx={{ mt: 10 }}>
                <CallToAction
                    title="Prêt à trouver ton stage ou alternance ?"
                    subtitle="Passe l’entretien IA en quelques minutes et reçois des offres pertinentes chaque jour."
                    actions={
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                            <a href="https://apps.apple.com/fr/app/student-link/id6447442323" target="_blank" rel="noopener noreferrer">
                                <Box component="img" src="/assets/images/app-store.png" alt="Télécharger dans l’App Store" loading="lazy" decoding="async" sx={{ height: 48, width: 'auto' }} />
                            </a>
                            <a href="https://play.google.com/store/search?q=student%20link&c=apps" target="_blank" rel="noopener noreferrer">
                                <Box component="img" src="/assets/images/google-play.png" alt="Disponible sur Google Play" loading="lazy" decoding="async" sx={{ height: 48, width: 'auto' }} />
                            </a>
                            <Button component="a" href="/bientot-disponible" variant="contained" sx={{ height: 48, borderRadius: 4, px: 2.5 }}>Connexion</Button>
                        </Stack>
                    }
                />
            </Container>


        </Box>
    );
} 